import React, { useState, useEffect } from "react";
import {
  generateFullReportPDF,
  generateReportPDF,
} from "../utilities/generateReportPDF";
import { LoadingButton } from "../components/loadingButton";
import { FormGroup } from "../components/FormItems";
import nationalitiesData from "../data/Nationalities.json";
import supervisorsData from "../data/eacSupervisors.json";
import depts_unitsData from "../data/eacDepartments.json";
import ErrorModal from "../components/ErrorModal";
import { LuDownload } from "react-icons/lu";
import { IoPrintOutline } from "react-icons/io5";

const InternshipReport = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    status: "",
    department: "",
    supervisor: "",
    institution: "",
    nationality: "",
    duration: "",
  });

  const [error, setError] = useState(null); // State to hold the error message
  const [showErrorModal, setShowErrorModal] = useState(false); //state to show the error modal

  const [reports, setReports] = useState([]);

  const depts_units = ["Select Internship Department", ...depts_unitsData.units];

  const nationalityOptions = [
    { value: "", label: "Select Nationality" },
    ...nationalitiesData.nationalities.map((nat) => ({
      value: nat.nationality,
      label: nat.nationality,
    })),
  ];

  const supervisorOptions = [
    { value: "", label: "Select Internship Supervisor" },
    ...supervisorsData.supervisors.map((supervisor) => ({
      value: supervisor,
      label: supervisor,
    })),
  ];

  // Sample data fetching or function to fetch reports
  useEffect(() => {}, [filters]);

  // Handle filter input changes
  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const apiUrl = process.env.REACT_APP_API_URL;
  //TODO:Comment during production build
  // const apiUrl = "http://localhost:5000/";

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString(); // Use URLSearchParams to format filters
      const response = await fetch(
        `${apiUrl}api/filtered-applications?${queryParams}`
      );
      const data = await response.json();
      setReports(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError(error.message);
      setShowErrorModal(true);
      setLoading(false);
    }
  };

  const [stats, setStats] = useState({
    totalApplications: 0,
    approvedApplications: 0,
    underReviewApplications: 0,
    pendingApplications: 0,
  });

  useEffect(() => {
    const fetchInternshipStats = async () => {
      try {
        const response = await fetch(`${apiUrl}api/applications-stats`);

        if (!response.ok) {
          throw new Error("Failed to fetch stats");
        }

        const data = await response.json(); // Extract JSON data
        setStats({
          totalApplications: data.totalApplications || 0,
          approvedApplications: data.approvedApplications || 0,
          underReviewApplications: data.underReviewApplications || 0,
          pendingApplications: data.pendingApplications || 0,
        });
      } catch (error) {
        console.error("Error fetching internship stats:", error);
      }
    };

    fetchInternshipStats();
  }, []);

  return (
    <div className="container mx-auto pb-4 px-4 overflow-y-auto h-[calc(100vh-14rem)] shadow-lg">
      <h1 className="text-2xl md:text-3xl text-gray-600 font-bold mb-4 md:mb-6">
        Internship Reports
      </h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-blue-300 p-4 rounded-lg text-center">
          <h3 className="font-bold text-sm md:text-base">Total Applications</h3>
          <p className="text-2xl md:text-3xl text-red-700 font-semibold">
            {stats.totalApplications}
          </p>
        </div>
        <div className="bg-green-300 p-4 rounded-lg text-center">
          <h3 className="font-bold text-sm md:text-base">
            Approved Applications
          </h3>
          <p className="text-2xl md:text-3xl text-red-700 font-semibold">
            {stats.approvedApplications}
          </p>
        </div>
        <div className="bg-yellow-200 p-4 rounded-lg text-center">
          <h3 className="font-bold text-sm md:text-base">
            Applications Under Review
          </h3>
          <p className="text-2xl md:text-3xl text-red-700 font-semibold">
            {stats.underReviewApplications}
          </p>
        </div>
        <div className="bg-red-200 p-4 rounded-lg text-center">
          <h3 className="font-bold text-sm md:text-base">
            Pending Applications
          </h3>
          <p className="text-2xl md:text-3xl text-red-700 font-semibold">
            {stats.pendingApplications}
          </p>
        </div>
      </div>

      <div className="bg-gray-200 p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg md:text-xl font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <input
            type="date"
            name="startDate"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-sm md:text-base"
          />
          <input
            type="date"
            name="endDate"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-sm md:text-base"
          />
          <select
            name="status"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-gray-700 text-sm md:text-base"
          >
            <option value="">Internship Application Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="under review">Under Review</option>
            <option value="archived">Archived</option>
          </select>
          <FormGroup
            type="select"
            name="department"
            options={depts_units}
            onChange={handleFilterChange}
            className="font-semibold text-sm text-gray-700 md:text-base"
          />
          <select
            name="supervisor"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-gray-700 text-sm md:text-base"
          >
            {supervisorOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            name="institution"
            placeholder="Institution"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-gray-700 text-sm md:text-base"
          />
          <select
            name="nationality"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold text-gray-700 text-sm md:text-base"
          >
            {nationalityOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            name="activeFilter"
            onChange={handleFilterChange}
            className="border rounded p-2 font-semibold  text-gray-700text-sm md:text-base"
          >
            <option value="">Active / Completed Interns</option>
            <option value="active">Active Interns</option>
            <option value="completed">Completed Interns</option>
          </select>
        </div>

        <LoadingButton
          className="bg-blue-500 text-white px-4 py-2 font-semibold hover:bg-blue-600 text-sm md:text-base"
          onClick={handleGenerateReport}
          isLoading={loading}
          buttonText="Generate Report"
          loadingText="Generating...."
        />
      </div>

      <div className="bg-white py-4 rounded-lg overflow-x-auto">
        {/* Report Table */}
        <table className="min-w-full border-2 border-gray-200">
          <thead className="border-b text-gray-700">
            <tr className="bg-gray-300 text-sm md:text-base">
              <th className="px-3 py-4 text-base font-semibold w-12 text-center">
                #
              </th>
              <th className="p-3 text-left">Applicant Name</th>
              <th className="p-3 text-left">Department</th>
              <th className="p-3 text-left">Supervisor</th>
              <th className="p-3 text-left">Start Date</th>
              <th className="p-3 text-left">End Date</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.length > 0 ? (
              reports.map((report, index) => (
                <tr
                  key={report.id || index}
                  className={`hover:bg-gray-300 cursor-pointer text-sm md:text-base border-b border-gray-300 ${
                    index % 2 === 0
                      ? "bg-yellow-50 bg-opacity-50"
                      : "bg-gray-200 bg-opacity-50"
                  }`}
                >
                  <td className="px-3 py-4 font-medium w-12 text-center">
                    {index + 1}.
                  </td>
                  <td className="px-4 py-2">{`${report.surname} ${report.firstName} ${report.otherNames}`}</td>
                  <td className="px-4 py-2">{report.internshipDepartment}</td>
                  <td className="px-4 py-2">{report.internshipSupervisor}</td>
                  <td className="px-4 py-2">
                    {new Date(report.internshipStartDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    {new Date(report.internshipEndDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">{report.applicationStatus}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => generateReportPDF(report, "save")}
                      className="flex items-center gap-2 bg-green-700 text-white px-2 py-[6px] rounded hover:bg-green-600"
                    >
                      <LuDownload />
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No reports found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex flex-col lg:flex-row justify-start lg:gap-10 gap-3 mt-4">
          <button
            onClick={() => generateFullReportPDF(reports)}
            className="bg-green-700 py-2 px-8 text-white font-semibold rounded-md text-sm lg:mx-0 mx-auto md:text-base hover:bg-green-600 flex items-center gap-3"
          >
            <LuDownload size={20} /> Download Full Report as PDF
          </button>

          <button
            onClick={() => window.print()}
            className="bg-blue-500 px-16 lg:px-6 py-2 text-white font-semibold rounded-md lg:mx-0 mx-auto text-sm md:text-base hover:bg-blue-600"
          >
            <IoPrintOutline className="inline-block mr-2" /> Print Full Report
          </button>
        </div>
        {/* Error Modal */}
        <ErrorModal
          show={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={error}
        />
      </div>
    </div>
  );
};

export default InternshipReport;
